import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { FiUploadCloud, FiSave, FiX, FiPlus, FiTrash2, FiPackage, FiTag, FiDownloadCloud, FiZap, FiLoader } from 'react-icons/fi';
import { toast } from 'react-toastify';
import API from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import ServiceLocationPicker from '../../components/merchant/ServiceLocationPicker';
import DealCitiesPicker from '../../components/merchant/DealCitiesPicker';
import { validateImageFiles } from '../../utils/imageValidation';
import { dominantColorFromUrl, assignColorsToImages } from '../../utils/colorDetect';

const MAX_IMAGES = 8;

// ===== Caractéristiques intelligentes (produits physiques) =====
const ATTR_DEFS = {
  size:     { label: 'Taille', presets: ['XS', 'S', 'M', 'L', 'XL', 'XXL', '3XL'], kind: 'list', ph: 'Ex. 42, Unique…' },
  shoe:     { label: 'Pointure', presets: ['36', '37', '38', '39', '40', '41', '42', '43', '44', '45'], kind: 'list', ph: 'Ex. 47' },
  color:    { label: 'Couleur', presets: ['Noir', 'Blanc', 'Gris', 'Rouge', 'Bleu', 'Vert', 'Jaune', 'Rose', 'Orange', 'Marron', 'Beige', 'Violet'], kind: 'color', ph: 'Ex. Bordeaux' },
  volume:   { label: 'Volume / contenance', presets: ['100', '250', '500', '750', '1000'], units: ['ml', 'cl', 'L'], kind: 'measure', ph: 'Ex. 330' },
  weight:   { label: 'Poids / grammage', presets: ['100', '250', '500', '1000', '2000', '5000'], units: ['g', 'kg'], kind: 'measure', ph: 'Ex. 750' },
  capacity: { label: 'Capacité / stockage', presets: ['32', '64', '128', '256', '512', '1024'], units: ['Go', 'To'], kind: 'measure', ph: 'Ex. 128' },
  material: { label: 'Matière', presets: ['Coton', 'Cuir', 'Bois', 'Métal', 'Plastique', 'Verre', 'Inox', 'Laine'], kind: 'list', ph: 'Ex. Velours' },
  custom:   { label: 'Autre (personnalisé)', presets: [], kind: 'free', ph: 'Ex. Pack 3 pièces' },
};
const ATTR_ORDER = ['size', 'shoe', 'color', 'volume', 'weight', 'capacity', 'material', 'custom'];
const COLOR_HEX = {
  Noir: '#111827', Blanc: '#f3f4f6', Gris: '#9ca3af', Rouge: '#ef4444', Bleu: '#3b82f6',
  Vert: '#22c55e', Jaune: '#eab308', Rose: '#ec4899', Orange: '#f97316', Marron: '#92400e',
  Beige: '#e7d3b3', Violet: '#8b5cf6'
};
const attrFmt = (type, unit, val) => (ATTR_DEFS[type] && ATTR_DEFS[type].kind === 'measure') ? `${val}${unit || ''}` : String(val);

const buildImageUrl = (img) => {
  if (!img) return '';
  if (typeof img !== 'string') return '';
  if (/^https?:\/\//i.test(img)) return img;
  const base = API.defaults?.baseURL || (process.env.REACT_APP_API_URL || '');
  let s = String(img).trim();
  if (s.startsWith('/uploads/')) s = s.replace(/^\/uploads\//, '/api/uploads/');
  if (s.startsWith('uploads/')) s = s.replace(/^uploads\//, '/api/uploads/');
  if (!s.startsWith('/api/uploads') && !s.startsWith('/')) s = `/api/uploads/${s.replace(/^\/+/, '')}`;
  try { return new URL(s, base || window.location.origin).toString(); } catch { return s; }
};

const normalizeDealIncoming = (d) => {
  if (!d || typeof d !== 'object') return {};
  const description = d.description ?? d.desc ?? d.body ?? '';
  const originalPrice = d.originalPrice ?? d.original_price ?? d.origPrice ?? d.price ?? '';
  const discountedPrice = d.discountedPrice ?? d.discounted_price ?? d.discounted ?? d.price ?? '';
  // PIEGE DEJA RENCONTRE SUR CE FORMULAIRE (lieux de prestation) : un champ
  // absent d'ici revient vide dans le formulaire, et l'enregistrement EFFACE
  // la valeur en base. Un accord commercial efface en silence, ce serait un
  // partenaire paye a la mauvaise regle sans que personne ne le voie.
  // ABSENT (champ non renvoye) et VIDE (null) ne veulent PAS dire la meme
  // chose. Le serveur retire `partnerPrice` des reponses PUBLIQUES : en mode
  // support, ou pour une annonce d'un autre marchand, le formulaire recevait
  // donc une fiche sans ce champ, l'affichait vide, et l'enregistrement
  // ECRASAIT la valeur reelle en base.
  //
  // `partnerPriceLoaded` retient si l'information etait presente. Si elle ne
  // l'etait pas, on n'y touche pas a l'enregistrement.
  const partnerPriceLoaded = Object.prototype.hasOwnProperty.call(d || {}, 'partnerPrice');
  const partnerPrice = (d.partnerPrice === null || d.partnerPrice === undefined) ? '' : String(d.partnerPrice);
  let category = '';
  if (d.categoryId != null && d.categoryId !== '') {
    // ✅ priorité à l'id (le select est indexé par id)
    category = String(d.categoryId);
  } else if (d.category) {
    if (typeof d.category === 'string') category = d.category;
    else category = d.category._id || d.category.id || d.category.slug || d.category.name || '';
  }
  const startDate = d.startDate ?? d.start_date ?? d.validFrom ?? d.valid_from ?? d.from ?? '';
  const endDate = d.endDate ?? d.end_date ?? d.validUntil ?? d.valid_until ?? d.until ?? '';
  let locationVal = '';
  if (Array.isArray(d.locations) && d.locations.length) locationVal = d.locations[0];
  else if (d.location) locationVal = d.location;
  else if (d.address) locationVal = d.address;
  else if (Array.isArray(d.addresses) && d.addresses.length) locationVal = d.addresses[0];
  let terms = '';
  if (Array.isArray(d.conditions) && d.conditions.length) terms = d.conditions.join('\n');
  else if (d.conditions && typeof d.conditions === 'string') terms = d.conditions;
  else if (Array.isArray(d.terms) && d.terms.length) terms = d.terms.join('\n');
  else if (d.terms && typeof d.terms === 'string') terms = d.terms;
  let highlights = [''];
  try {
    if (Array.isArray(d.highlights) && d.highlights.length) highlights = d.highlights;
    else if (d.highlights && typeof d.highlights === 'string') {
      try {
        const parsed = JSON.parse(d.highlights);
        if (Array.isArray(parsed)) highlights = parsed;
        else highlights = [String(parsed)];
      } catch (_) {
        highlights = d.highlights.split('\n').map(s => s.trim()).filter(Boolean);
      }
    } else if (d.points || d.pointsFort || d.featureList) {
      const alt = d.points || d.pointsFort || d.featureList;
      if (Array.isArray(alt)) highlights = alt;
      else if (typeof alt === 'string') highlights = alt.split('\n').map(s => s.trim()).filter(Boolean);
    }
    if (!highlights || !highlights.length) highlights = [''];
  } catch (e) { highlights = ['']; }

  const imgsRaw = Array.isArray(d.images) ? d.images : (d.image ? (Array.isArray(d.image) ? d.image : [d.image]) : []);
  const images = imgsRaw.map(i => {
    if (!i) return '';
    if (typeof i === 'string') return i;
    if (i.url) return i.url;
    if (i.path) return i.path;
    if (i.filename) return i.filename;
    return '';
  }).filter(Boolean);

  const quantity = d.quantity ?? d.qty ?? 0;
  const unlimited = (d.unlimited === true) || (Number(d.quantityTotal) <= 0 && d.quantityTotal != null) || Number(quantity) <= 0;

  // ===== Marketplace multi-types =====
  const productType = d.productType || 'service';
  const shippingFee = (d.shippingFee != null && d.shippingFee !== '') ? d.shippingFee : '';
  const freeShippingThreshold = (d.freeShippingThreshold != null && d.freeShippingThreshold !== '') ? d.freeShippingThreshold : '';
  const weightGrams = (d.weightGrams != null && d.weightGrams !== '') ? d.weightGrams : '';
  let variants = Array.isArray(d.variants) ? d.variants : [];
  if (typeof d.variants === 'string') { try { const p = JSON.parse(d.variants); if (Array.isArray(p)) variants = p; } catch (e) {} }
  variants = variants.map(v => ({ label: String(v.label || ''), priceDelta: v.priceDelta ?? '', stock: v.stock ?? '' }));
  let attributes = Array.isArray(d.attributes) ? d.attributes : [];
  if (typeof d.attributes === 'string') { try { const p = JSON.parse(d.attributes); if (Array.isArray(p)) attributes = p; } catch (e) {} }
  attributes = attributes.map(a => ({ type: a.type, unit: a.unit ?? null, values: Array.isArray(a.values) ? a.values : [] }));
  let shippingZones = Array.isArray(d.shippingZones) ? d.shippingZones : [];
  if (typeof d.shippingZones === 'string') { try { const p = JSON.parse(d.shippingZones); if (Array.isArray(p)) shippingZones = p; } catch (e) {} }
  shippingZones = shippingZones.map(z => ({ name: String(z.name || ''), cities: Array.isArray(z.cities) ? z.cities : [], fee: z.fee ?? '', freeThreshold: z.freeThreshold ?? '' }));
  // Lot D — mode de tarif + paliers au poids
  const shippingMode = d.shippingMode === 'weight' ? 'weight' : 'flat';
  let shippingWeightTiers = Array.isArray(d.shippingWeightTiers) ? d.shippingWeightTiers : [];
  if (typeof d.shippingWeightTiers === 'string') { try { const p = JSON.parse(d.shippingWeightTiers); if (Array.isArray(p)) shippingWeightTiers = p; } catch (e) {} }
  shippingWeightTiers = shippingWeightTiers.map(t => ({ uptoGrams: t.uptoGrams ?? '', fee: t.fee ?? '' }));
  let colorImageMap = (d.colorImageMap && typeof d.colorImageMap === 'object' && !Array.isArray(d.colorImageMap)) ? d.colorImageMap : {};
  if (typeof d.colorImageMap === 'string') { try { const p = JSON.parse(d.colorImageMap); if (p && typeof p === 'object' && !Array.isArray(p)) colorImageMap = p; } catch (e) {} }
  const title = d.title ?? d.name ?? '';
  return {
    title,
    description,
    originalPrice,
    price: discountedPrice,
    partnerPrice,
    partnerPriceLoaded,
    partnerPriceHidden: d.partnerPriceHidden === true,
    category,
    validFrom: startDate,
    validUntil: endDate,
    quantity,
    unlimited,
    location: locationVal,
    terms,
    // CORRECTIF : ces champs n'etaient pas remontes -> le formulaire d'edition
    // s'ouvrait vide et ecrasait l'adresse / les horaires en base a l'enregistrement.
    addressFull: d.addressFull ?? '',
    addressLat: d.addressLat ?? '',
    addressLng: d.addressLng ?? '',
    openingHours: d.openingHours ?? '',
    durationMinutes: d.durationMinutes ?? '',
    instructions: d.instructions ?? '',
    serviceLocationId: d.serviceLocationId ?? '',
    status: d.status ?? '',
    rejectionReason: d.rejectionReason ?? '',
    highlights,
    images,
    productType,
    shippingFee,
    freeShippingThreshold,
    weightGrams,
    variants,
    attributes,
    shippingZones,
    shippingMode,
    shippingWeightTiers,
    colorImageMap,
    // ===== Fiche produit =====
    // Valeurs telles que renvoyees par l'API ; le formulaire les renvoie
    // ensuite systematiquement pour que rien ne soit efface par omission.
    condition: d.condition === 'used' ? 'used' : 'new',
    availability: ['in_stock', 'out_of_stock', 'incoming'].includes(d.availability) ? d.availability : 'in_stock',
    brandId: d.brandId ?? null,
    brand: d.brand ?? '',
    sku: d.sku ?? '',
    barcode: d.barcode ?? '',
    expiryDate: d.expiryDate ? String(d.expiryDate).slice(0, 10) : '',
  };
};

// Selecteur de marque avec logo (catalogue admin + saisie libre en secours)
import BrandPicker from '../../components/merchant/BrandPicker';
import QuantityTiersEditor from '../../components/merchant/QuantityTiersEditor';
import InfoSectionsEditor from '../../components/merchant/InfoSectionsEditor';

const EditDeal = () => {
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const location = useLocation();
  const { user } = useAuth();
  const mountedRef = useRef(true);

  const [loading, setLoading] = useState(false);
  const [categories, setCategories] = useState([]); // ✅ pour le select Catégorie
  const [uploadProgress, setUploadProgress] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    originalPrice: '',
    partnerPrice: '',
    partnerPriceLoaded: false,
    partnerPriceHidden: false,
    serviceLocationIds: [],
    price: '',
    category: '',
    validFrom: '',
    validUntil: '',
    openEnded: false, // ✅ validité ouverte (sans date de fin)
    quantity: '',
    unlimited: false,
    quantityTiers: [],
    infoSections: [],
    location: '',
    terms: '',
    // ===== Lot P1 — Infos de prestation =====
    // addressFull / instructions ne sont jamais publics : communiqués au client
    // sur son coupon, après paiement uniquement.
    addressFull: '',
    addressLat: '',
    addressLng: '',
    openingHours: '',
    durationMinutes: '',
    instructions: '',
    // Lieu de prestation reutilisable ('' = saisie manuelle propre a cette annonce)
    serviceLocationId: '',
    status: '',
    rejectionReason: '',
    saveAsServiceLocation: false,
    serviceLocationLabel: '',
    highlights: [''],
    images: [], // mix: existing urls (string) + new File objects appended for form convenience (we manage separately)
    productType: 'service',
    shippingFee: '',
    freeShippingThreshold: '',
    weightGrams: '',
    variants: [],
    attributes: [],
    shippingZones: [],
    shippingMode: 'flat',
    shippingWeightTiers: [],
    colorImageMap: {},
    condition: 'new',
    availability: 'in_stock',
    brandId: null,
    brand: '',
    sku: '',
    barcode: '',
    expiryDate: '',
  });
  const [errors, setErrors] = useState({});
  const [attrInputs, setAttrInputs] = useState({});
  const imagePreviewsRef = useRef([]);
  const [existingImages, setExistingImages] = useState([]); // urls
  const [newFiles, setNewFiles] = useState([]);

  // Champs de prestation verrouillés quand ils proviennent d'un lieu enregistré :
  // ils se modifient depuis Paramètres > Lieux de prestation, pas annonce par annonce.
  const locked = !!formData.serviceLocationId;

  // Application d'un lieu enregistré ; `null` = retour à la saisie manuelle.
  const applyServiceLocation = (loc) => {
    setFormData((f) => (loc ? {
      ...f,
      serviceLocationId: String(loc.id),
      addressFull: loc.addressFull || '',
      addressLat: loc.addressLat ?? '',
      addressLng: loc.addressLng ?? '',
      openingHours: loc.openingHours || '',
      instructions: loc.instructions || '',
      saveAsServiceLocation: false,
      serviceLocationLabel: ''
    } : { ...f, serviceLocationId: '' }));
    setErrors((e) => ({ ...e, addressFull: undefined }));
  };

  useEffect(() => {
    mountedRef.current = true;
    // ✅ charge les catégories pour le select
    (async () => {
      try {
        const { data } = await API.get('/categories');
        if (mountedRef.current) setCategories(data?.data || data || []);
      } catch (e) { console.warn('categories load failed', e); }
    })();
    const incoming = location?.state?.deal;
    if (incoming && mountedRef.current) {
      applyIncoming(incoming);
    } else if (editId) {
      fetchDealById(editId);
    }
    return () => {
      mountedRef.current = false;
      imagePreviewsRef.current.forEach(u => { try { URL.revokeObjectURL(u); } catch (e) {} });
      imagePreviewsRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location?.state, editId]);

  const applyIncoming = (d) => {
    const n = normalizeDealIncoming(d);
    const fmtDate = (v) => {
      if (!v) return '';
      try { const dt = new Date(v); if (isNaN(dt)) return ''; return dt.toISOString().slice(0,10); } catch { return ''; }
    };
    const validUntilStr = fmtDate(n.validUntil);
    const preset = {
      title: n.title || '',
      description: n.description || '',
      originalPrice: n.originalPrice ?? '',
      partnerPrice: n.partnerPrice ?? '',
      partnerPriceLoaded: n.partnerPriceLoaded === true,
      partnerPriceHidden: n.partnerPriceHidden === true,
      // Lieux deja rattaches : sans cette reprise, ouvrir puis enregistrer
      // une annonce viderait ses villes. Meme piege que les lieux de
      // prestation en v1.396.0.
      serviceLocationIds: Array.isArray(d.serviceLocations)
        ? d.serviceLocations.map((l) => String(l.locationId || l.id)).filter(Boolean)
        : [],
      price: n.price ?? '',
      category: n.category || '',
      validFrom: fmtDate(n.validFrom),
      validUntil: validUntilStr,
      openEnded: !validUntilStr, // ✅ pas de date de fin => validité ouverte
      quantity: n.quantity ?? '',
      quantityTiers: Array.isArray(n.quantityTiers) ? n.quantityTiers : [],
      infoSections: Array.isArray(n.infoSections) ? n.infoSections : [],
      unlimited: (n.unlimited === true) || (Number(n.quantityTotal) <= 0 && n.quantityTotal != null),
      location: n.location || '',
      terms: n.terms || '',
      addressFull: n.addressFull || '',
      addressLat: n.addressLat ?? '',
      addressLng: n.addressLng ?? '',
      openingHours: n.openingHours || '',
      durationMinutes: n.durationMinutes ?? '',
      instructions: n.instructions || '',
      serviceLocationId: n.serviceLocationId || '',
      status: n.status || '',
      rejectionReason: n.rejectionReason || '',
      saveAsServiceLocation: false,
      serviceLocationLabel: '',
      highlights: Array.isArray(n.highlights) && n.highlights.length ? n.highlights : [''],
      productType: n.productType || 'service',
      shippingFee: n.shippingFee ?? '',
      freeShippingThreshold: n.freeShippingThreshold ?? '',
      weightGrams: n.weightGrams ?? '',
      variants: Array.isArray(n.variants) ? n.variants : [],
      attributes: Array.isArray(n.attributes) ? n.attributes : [],
      shippingZones: Array.isArray(n.shippingZones) ? n.shippingZones : [],
      shippingMode: n.shippingMode === 'weight' ? 'weight' : 'flat',
      shippingWeightTiers: Array.isArray(n.shippingWeightTiers) ? n.shippingWeightTiers : [],
      colorImageMap: (n.colorImageMap && typeof n.colorImageMap === 'object') ? n.colorImageMap : {},
      condition: n.condition === 'used' ? 'used' : 'new',
      availability: ['in_stock', 'out_of_stock', 'incoming'].includes(n.availability) ? n.availability : 'in_stock',
      brandId: n.brandId ?? null,
      brand: n.brand ?? '',
      sku: n.sku ?? '',
      barcode: n.barcode ?? '',
      expiryDate: n.expiryDate ? String(n.expiryDate).slice(0, 10) : '',
    };
    const imgs = Array.isArray(n.images) ? n.images : [];
    if (!mountedRef.current) return;
    setFormData(prev => ({ ...prev, ...preset, images: imgs }));
    setExistingImages(imgs);
    imagePreviewsRef.current = imgs.map(buildImageUrl);
  };

  const fetchDealById = async (id) => {
    setLoading(true);
    try {
      // try admin endpoint first, then public
      let res = null;
      try { res = await API.get(`/admin/deals/${id}`); }
      catch (e1) {
        try { res = await API.get(`/deals/id/${id}`); }
        catch (e2) {
          try { res = await API.get(`/merchant/deals/${id}`); } catch (e3) { throw e3; }
        }
      }
      const payload = res?.data?.data ?? res?.data ?? res;
      const deal = Array.isArray(payload) ? payload[0] : payload;
      if (deal) applyIncoming(deal);
      else throw new Error('Deal introuvable');
    } catch (err) {
      console.error('fetchDealById', err);
      toast.error('Impossible de charger le deal');
    } finally {
      if (mountedRef.current) setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: undefined }));
  };

  // ===== Variantes =====
  const addVariant = () => setFormData(prev => ({ ...prev, variants: [...(prev.variants || []), { label: '', priceDelta: '', stock: '' }] }));
  const removeVariant = (i) => setFormData(prev => ({ ...prev, variants: (prev.variants || []).filter((_, idx) => idx !== i) }));
  const updateVariant = (i, key, val) => setFormData(prev => {
    const arr = [...(prev.variants || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...prev, variants: arr };
  });

  // ===== Caractéristiques intelligentes =====
  const getAttr = (type) => (formData.attributes || []).find(a => a.type === type);
  const toggleAttribute = (type) => setFormData(prev => {
    const list = prev.attributes || [];
    if (list.some(a => a.type === type)) return { ...prev, attributes: list.filter(a => a.type !== type) };
    const def = ATTR_DEFS[type];
    return { ...prev, attributes: [...list, { type, unit: def.units ? def.units[0] : null, values: [] }] };
  });
  const setAttrUnit = (type, unit) => setFormData(prev => ({ ...prev, attributes: (prev.attributes || []).map(a => a.type === type ? { ...a, unit } : a) }));
  const addAttrValue = (type, raw) => {
    const val = String(raw || '').trim();
    if (!val) return;
    setFormData(prev => ({
      ...prev,
      attributes: (prev.attributes || []).map(a => (a.type === type && !a.values.includes(val)) ? { ...a, values: [...a.values, val] } : a)
    }));
  };
  const removeAttrValue = (type, val) => setFormData(prev => ({
    ...prev,
    attributes: (prev.attributes || []).map(a => a.type === type ? { ...a, values: a.values.filter(v => v !== val) } : a)
  }));
  const generateVariants = () => {
    const active = (formData.attributes || []).filter(a => a.values && a.values.length);
    if (!active.length) { toast.error('Activez au moins une caractéristique et ajoutez des valeurs'); return; }
    const dims = active.map(a => a.values.map(v => attrFmt(a.type, a.unit, v)));
    let combos = [[]];
    dims.forEach(vals => {
      const next = [];
      combos.forEach(c => vals.forEach(v => next.push([...c, v])));
      combos = next;
    });
    if (combos.length > 60) { toast.error(`Trop de combinaisons (${combos.length}). Limite : 60.`); return; }
    const prevByLabel = {};
    (formData.variants || []).forEach(v => { prevByLabel[v.label] = v; });
    const variants = combos.map(parts => {
      const label = parts.join(' / ');
      const ex = prevByLabel[label];
      return { label, priceDelta: ex ? ex.priceDelta : '', stock: ex ? ex.stock : '' };
    });
    setFormData(prev => ({ ...prev, variants }));
    toast.success(`${variants.length} variante(s) générée(s)`);
  };
  const setProductType = (productType) => setFormData(prev => ({ ...prev, productType }));

  // ===== Zones de livraison =====
  const addZone = () => setFormData(prev => ({ ...prev, shippingZones: [...(prev.shippingZones || []), { name: '', cities: [], fee: '', freeThreshold: '' }] }));
  const removeZone = (i) => setFormData(prev => ({ ...prev, shippingZones: (prev.shippingZones || []).filter((_, idx) => idx !== i) }));
  const updateZone = (i, key, val) => setFormData(prev => {
    const arr = [...(prev.shippingZones || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...prev, shippingZones: arr };
  });
  const updateZoneCities = (i, raw) => setFormData(prev => {
    const arr = [...(prev.shippingZones || [])];
    arr[i] = { ...arr[i], cities: String(raw).split(',').map(s => s.trim()) };
    return { ...prev, shippingZones: arr };
  });

  // ===== Paliers de poids (Lot D) =====
  const addTier = () => setFormData(prev => ({ ...prev, shippingWeightTiers: [...(prev.shippingWeightTiers || []), { uptoGrams: '', fee: '' }] }));
  const removeTier = (i) => setFormData(prev => ({ ...prev, shippingWeightTiers: (prev.shippingWeightTiers || []).filter((_, idx) => idx !== i) }));
  const updateTier = (i, key, val) => setFormData(prev => {
    const arr = [...(prev.shippingWeightTiers || [])];
    arr[i] = { ...arr[i], [key]: val };
    return { ...prev, shippingWeightTiers: arr };
  });

  // ===== Image par couleur =====
  const setColorImage = (color, idx) => setFormData(prev => {
    const map = { ...(prev.colorImageMap || {}) };
    if (idx == null) delete map[color]; else map[color] = idx;
    return { ...prev, colorImageMap: map };
  });

  // Détection auto : couleur dominante de chaque image -> association à la bonne couleur
  const [detecting, setDetecting] = useState(false);
  const autoDetectColors = async () => {
    const colorAttr = (formData.attributes || []).find((a) => a.type === 'color' && Array.isArray(a.values) && a.values.length);
    const urls = combinedImagesForRender();
    if (!colorAttr || urls.length === 0) { toast.info('Ajoutez des images et au moins une couleur d\'abord.'); return; }
    setDetecting(true);
    try {
      const imageColors = await Promise.all(urls.map((u) => dominantColorFromUrl(u)));
      const { map, unresolved } = assignColorsToImages(colorAttr.values, imageColors, COLOR_HEX);
      if (Object.keys(map).length) {
        setFormData((prev) => ({ ...prev, colorImageMap: { ...map } }));
        const n = Object.keys(map).length;
        toast.success(`${n} couleur${n > 1 ? 's' : ''} associée${n > 1 ? 's' : ''} automatiquement. Vérifiez et ajustez si besoin.`);
      } else {
        toast.info('Aucune couleur n\'a pu être détectée automatiquement.');
      }
      if (unresolved.length) toast.info(`À associer manuellement : ${unresolved.join(', ')}.`);
    } finally {
      setDetecting(false);
    }
  };

  const handleHighlightChange = (i, v) => {
    const arr = [...formData.highlights]; arr[i] = v; setFormData(prev => ({ ...prev, highlights: arr }));
  };
  const addHighlight = () => setFormData(prev => ({ ...prev, highlights: [...prev.highlights, ''] }));
  const removeHighlight = (i) => setFormData(prev => ({ ...prev, highlights: prev.highlights.filter((_, idx) => idx !== i) }));

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    e.target.value = null;
    if (!files.length) return;
    const totalExisting = existingImages.length + newFiles.length;
    const spaceLeft = Math.max(0, MAX_IMAGES - totalExisting);
    const { valid, rejected } = await validateImageFiles(files.slice(0, spaceLeft));
    rejected.forEach((r) => toast.warn(`${r.name} ignoré : ${r.reason}`));
    if (!valid.length) return;
    const urls = valid.map(f => URL.createObjectURL(f));
    imagePreviewsRef.current = [...imagePreviewsRef.current, ...urls];
    setNewFiles(prev => [...prev, ...valid]);
    setFormData(prev => ({ ...prev, images: [...(prev.images || []), ...valid] }));
    setErrors(prev => ({ ...prev, images: undefined }));
  };

  // ------------------------------------------------------------------
  // ORDRE DES PHOTOS. La 1re est l'image principale.
  //
  // L'ecran manipule DEUX listes : `existingImages` (deja en ligne) et
  // `newFiles` (a televerser). Reordonner ne peut donc pas se faire sur une
  // seule d'entre elles — on tient un ORDRE SEPARE, fait de jetons :
  //   - le nom d'une image existante
  //   - `__new__<i>` pour le i-eme fichier a televerser
  // C'est ce meme ordre qui part au serveur.
  //
  // Sans cela, une photo qu'on vient d'ajouter atterrissait toujours en
  // dernier : impossible d'en faire l'image principale sans enregistrer,
  // rouvrir, puis reordonner.
  // ------------------------------------------------------------------
  const [imageOrder, setImageOrder] = useState([]);
  const [dragIdx, setDragIdx] = useState(null);
  // L'utilisateur a-t-il TOUCHE au champ prix partenaire ? Sans ce suivi, on
  // ne pourrait jamais RENSEIGNER un prix sur une annonce qui n'en avait pas :
  // le champ n'ayant pas ete charge, il ne serait jamais envoye.
  const [partnerPriceTouched, setPartnerPriceTouched] = useState(false);

  // L'ordre suit les ajouts et suppressions : tout jeton disparu est retire,
  // tout jeton nouveau est ajoute a la fin.
  useEffect(() => {
    const jetons = [
      ...existingImages.map((u) => String(u)),
      ...newFiles.map((_, i) => `__new__${i}`),
    ];
    setImageOrder((prev) => {
      const gardes = prev.filter((t) => jetons.includes(t));
      const ajouts = jetons.filter((t) => !gardes.includes(t));
      return [...gardes, ...ajouts];
    });
  }, [existingImages, newFiles]);

  const reorderImages = (from, to) => {
    if (from == null || to == null || from === to) return;
    setImageOrder((prev) => {
      if (from < 0 || to < 0 || from >= prev.length || to >= prev.length) return prev;
      const next = prev.slice();
      const [item] = next.splice(from, 1);
      next.splice(to, 0, item);
      return next;
    });
  };

  // Aperçu d'un jeton, quel que soit son type.
  const previewFor = (token) => {
    const m = /^__new__(\d+)$/.exec(String(token));
    if (m) {
      const i = Number(m[1]);
      const f = newFiles[i];
      if (!f) return '';
      const k = existingImages.length + i;
      return imagePreviewsRef.current[k] || (() => {
        try { const u = URL.createObjectURL(f); imagePreviewsRef.current[k] = u; return u; } catch (e) { return ''; }
      })();
    }
    return buildImageUrl(token);
  };

  // Suppression par JETON : l'index affiche ne correspond plus a l'index dans
  // les listes d'origine des que l'ordre a change.
  const removeByToken = (token) => {
    const m = /^__new__(\d+)$/.exec(String(token));
    if (m) {
      const i = Number(m[1]);
      setNewFiles((prev) => prev.filter((_, k) => k !== i));
      setFormData((prev) => ({ ...prev, images: (prev.images || []).filter((_, k) => k !== (existingImages.length + i)) }));
    } else {
      setExistingImages((prev) => prev.filter((u) => String(u) !== String(token)));
    }
    setImageOrder((prev) => prev.filter((t) => t !== token));
  };

  const removeImageAt = (index) => {
    const totalExisting = existingImages.length;
    if (index < totalExisting) {
      const newExisting = existingImages.filter((_, i) => i !== index);
      setExistingImages(newExisting);
      setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
      try { URL.revokeObjectURL(imagePreviewsRef.current[index]); } catch (e) {}
      imagePreviewsRef.current = imagePreviewsRef.current.filter((_, i) => i !== index);
    } else {
      const newFileIndex = index - totalExisting;
      try { URL.revokeObjectURL(imagePreviewsRef.current[totalExisting + newFileIndex]); } catch (e) {}
      imagePreviewsRef.current = imagePreviewsRef.current.filter((_, i) => i !== (totalExisting + newFileIndex));
      setNewFiles(prev => prev.filter((_, i) => i !== newFileIndex));
      setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== (totalExisting + newFileIndex)) }));
    }
    setFormData(prev => {
      const map = { ...(prev.colorImageMap || {}) };
      Object.keys(map).forEach((c) => {
        const v = Number(map[c]);
        if (v === index) delete map[c];
        else if (v > index) map[c] = v - 1;
      });
      return { ...prev, colorImageMap: map };
    });
  };

  const calculateDiscount = () => {
    const orig = parseFloat(formData.originalPrice) || 0;
    const cur = parseFloat(formData.price) || 0;
    if (orig > 0 && cur > 0 && cur < orig) return Math.round(((orig - cur) / orig) * 100);
    return 0;
  };

  const validate = () => {
    const e = {};
    if (!formData.title?.trim()) e.title = 'Titre requis';
    if (!formData.description?.trim()) e.description = 'Description requise';
    const orig = parseFloat(formData.originalPrice);
    const price = parseFloat(formData.price);
    if (!Number.isFinite(orig) || orig < 0) e.originalPrice = 'Prix original invalide';
    if (!Number.isFinite(price) || price < 0) e.price = 'Prix invalide';
    if (Number.isFinite(orig) && Number.isFinite(price) && price > orig) e.price = 'Le prix doit être <= prix original';
    if (!formData.category) e.category = 'Catégorie requise';
    if (!formData.location?.trim()) e.location = 'Localisation requise';
    // Lot P1 : pour un service, l'adresse exacte est indispensable — c'est elle qui
    // sera figée sur le coupon et permettra au client de se rendre sur place.
    if (formData.productType === 'service' && !formData.serviceLocationId && !formData.addressFull?.trim()) {
      e.addressFull = 'Adresse exacte requise pour un service (communiquée au client après son achat)';
    }
    if (!formData.unlimited && (!formData.quantity || parseInt(formData.quantity, 10) < 0)) e.quantity = 'Quantité invalide';
    if (formData.validFrom && formData.validUntil && new Date(formData.validFrom) > new Date(formData.validUntil)) e.validUntil = 'Date fin doit être après début';
    const totalImages = (existingImages?.length || 0) + (newFiles?.length || 0);
    if (totalImages === 0) e.images = 'Au moins une image requise';
    if (totalImages > MAX_IMAGES) e.images = `Max ${MAX_IMAGES} images`;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) { toast.error('Corrige les erreurs'); return; }
    if (!editId) { toast.error('ID manquant'); return; }
    setLoading(true);
    setUploadProgress(null);

    try {
      const payload = new FormData();
      payload.append('title', (formData.title || '').trim());
      payload.append('description', (formData.description || '').trim());
      payload.append('originalPrice', String(Number(formData.originalPrice || 0)));
      payload.append('discountedPrice', String(Number(formData.price || 0)));
      // Envoye MEME VIDE : c'est ce qui permet de revenir au mode commission.
      // Le serveur lit '' comme « plus de prix d'achat », et un champ absent
      // comme « ne touche pas a la valeur existante ».
      // Envoye UNIQUEMENT si la valeur a bien ete chargee. Sinon le champ est
      // omis, et le serveur conserve ce qu'il a en base — c'est ce qui evite
      // d'effacer un prix qu'on n'avait pas le droit de voir.
      if (formData.partnerPriceLoaded || partnerPriceTouched) {
        payload.append('partnerPrice', String(formData.partnerPrice ?? '').trim());
      }
      // Envoye MEME VIDE : c'est ce qui permet de tout decocher. Le serveur
      // distingue « champ absent » (ne touche a rien) de « champ vide ».
      const villes = formData.serviceLocationIds || [];
      if (villes.length) villes.forEach((id) => payload.append('serviceLocationIds', id));
      else payload.append('serviceLocationIds', '');
      payload.append('price', String(Number(formData.price || 0)));
      payload.append('category', formData.category || '');
      payload.append('quantity', formData.unlimited ? '0' : String(Math.max(0, parseInt(formData.quantity || '0', 10))));
      payload.append('quantityTiers', JSON.stringify(formData.quantityTiers || []));
      payload.append('infoSections', JSON.stringify(formData.infoSections || []));
      payload.append('location', formData.location || '');
      if (formData.validFrom) { payload.append('validFrom', formData.validFrom); payload.append('startDate', new Date(formData.validFrom).toISOString()); }
      // ✅ POINT 5 : validité ouverte -> on signale openEnded et on n'envoie pas endDate
      payload.append('openEnded', formData.openEnded ? 'true' : 'false');
      if (!formData.openEnded && formData.validUntil) { payload.append('validUntil', formData.validUntil); payload.append('endDate', new Date(formData.validUntil).toISOString()); }
      payload.append('terms', formData.terms || '');

      // ===== Lot P1 — Infos de prestation (envoyées même vides pour permettre l'effacement)
      payload.append('addressFull', formData.addressFull || '');
      payload.append('instructions', formData.instructions || '');
      payload.append('openingHours', formData.openingHours || '');
      if (formData.addressLat !== '') payload.append('addressLat', formData.addressLat);
      if (formData.addressLng !== '') payload.append('addressLng', formData.addressLng);
      if (formData.durationMinutes !== '') payload.append('durationMinutes', formData.durationMinutes);
      // Rattachement au lieu de prestation enregistre dans le profil marchand.
      payload.append('serviceLocationId', formData.serviceLocationId || '');
      if (!formData.serviceLocationId && formData.saveAsServiceLocation) {
        payload.append('saveAsServiceLocation', 'true');
        payload.append('serviceLocationLabel', formData.serviceLocationLabel || '');
      }

      // ===== Fiche produit =====
      // Les champs vides sont envoyes explicitement (chaine vide) : c'est ce qui
      // permet au marchand d'EFFACER une marque ou une date de peremption.
      if (formData.productType !== 'service') {
        payload.append('availability', formData.availability || 'in_stock');
        // brandId prime : le serveur recopie le nom depuis la fiche marque.
        payload.append('brandId', formData.brandId ? String(formData.brandId) : '');
        payload.append('brand', formData.brand.trim());
        payload.append('sku', formData.sku.trim());
        if (formData.productType === 'physical') {
          payload.append('condition', formData.condition || 'new');
          payload.append('barcode', formData.barcode.trim());
          payload.append('expiryDate', formData.expiryDate || '');
        }
      }

      // ===== Marketplace multi-types =====
      payload.append('productType', formData.productType || 'service');
      if (formData.productType === 'physical') {
        payload.append('shippingFee', String(Number(formData.shippingFee || 0)));
        payload.append('freeShippingThreshold', String(formData.freeShippingThreshold) !== '' ? String(Number(formData.freeShippingThreshold)) : '');
        payload.append('weightGrams', String(formData.weightGrams) !== '' ? String(parseInt(formData.weightGrams, 10) || 0) : '');
        const variants = (formData.variants || [])
          .map(v => ({ label: String(v.label || '').trim(), priceDelta: Number(v.priceDelta || 0), stock: parseInt(v.stock || '0', 10) || 0 }))
          .filter(v => v.label);
        payload.append('variants', JSON.stringify(variants));
        const attributes = (formData.attributes || [])
          .map(a => ({ type: a.type, unit: a.unit || null, values: (a.values || []).map(v => String(v).trim()).filter(Boolean) }))
          .filter(a => a.type && a.values.length);
        payload.append('attributes', JSON.stringify(attributes));
        const shippingZones = (formData.shippingZones || [])
          .map(z => ({ name: String(z.name || '').trim(), cities: (z.cities || []).map(c => String(c).trim()).filter(Boolean), fee: Number(z.fee || 0), freeThreshold: String(z.freeThreshold) !== '' ? Number(z.freeThreshold) : null }))
          .filter(z => z.name && z.cities.length);
        payload.append('shippingZones', JSON.stringify(shippingZones));
        // Lot D — mode de tarif + paliers au poids
        const shippingMode = formData.shippingMode === 'weight' ? 'weight' : 'flat';
        payload.append('shippingMode', shippingMode);
        const tiers = shippingMode === 'weight'
          ? (formData.shippingWeightTiers || [])
              .map(t => ({ uptoGrams: parseInt(t.uptoGrams, 10) || 0, fee: Number(t.fee || 0) }))
              .filter(t => t.uptoGrams > 0)
              .sort((a, b) => a.uptoGrams - b.uptoGrams)
          : [];
        payload.append('shippingWeightTiers', JSON.stringify(tiers));
        payload.append('colorImageMap', JSON.stringify(formData.colorImageMap || {}));
      }
      const highlights = (formData.highlights || []).map(h => h.trim()).filter(Boolean);
      if (highlights.length) payload.append('highlights', JSON.stringify(highlights));

      // existing image urls to keep
      // Images conservees, DANS L'ORDRE choisi a l'ecran, puis l'ordre complet
      // (jetons) pour que le serveur sache ou intercaler les nouveaux fichiers.
      const ordre = (imageOrder && imageOrder.length)
        ? imageOrder
        : [...(existingImages || []).map(String), ...(newFiles || []).map((_, i) => `__new__${i}`)];
      ordre.filter((t) => !/^__new__\d+$/.test(String(t)))
           .forEach((url) => payload.append('existingImages', url));
      ordre.forEach((t) => payload.append('imageOrder', t));
      // new files
      (newFiles || []).forEach(f => payload.append('images', f));

      const config = {
        onUploadProgress: (evt) => {
          if (evt && evt.total) {
            const pct = Math.round((evt.loaded * 100) / evt.total);
            if (mountedRef.current) setUploadProgress(pct);
          }
        }
      };

      // prefer admin update route if admin user; try several fallbacks
      let res;
      const tryEndpoints = [];
      if (user && (typeof user.role === 'string' ? user.role.toLowerCase().includes('admin') : (Array.isArray(user.roles) && user.roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase().includes('admin'))))) {
        tryEndpoints.push(`/admin/deals/${editId}`, `/admin/deals/id/${editId}`);
      }
      tryEndpoints.push(`/merchant/deals/${editId}`, `/deals/id/${editId}`, `/deals/${editId}`);

      let lastErr;
      for (const ep of tryEndpoints) {
        try {
          res = await API.put(ep, payload, config);
          break;
        } catch (e) { lastErr = e; }
      }
      if (!res) throw lastErr;

      const data = res?.data ?? res;
      if (data?.success) {
        // `moderationPending` : le serveur a renvoyé l'annonce en validation admin
        // parce qu'un champ vu par l'acheteur (titre, photos, texte, prix) a changé.
        if (data.moderationPending) {
          toast.info(data.message || 'Modifications enregistrées — en attente de validation', { autoClose: 8000 });
        } else {
          toast.success(data.message || 'Deal mis à jour');
        }
        if (!mountedRef.current) return;
        navigate((user && (typeof user.role === 'string' ? user.role.toLowerCase().includes('admin') : (Array.isArray(user.roles) && user.roles.some(r => (typeof r === 'string' ? r : r?.name || '').toLowerCase().includes('admin'))))) ? '/admin/deals' : '/merchant/deals');
      } else {
        toast.error(data?.message || 'Erreur mise à jour');
        if (data?.errors) setErrors(prev => ({ ...prev, ...data.errors }));
      }
    } catch (err) {
      console.error('update error', err);
      const msg = err?.response?.data?.message || err?.message || 'Erreur';
      const srv = err?.response?.data?.errors || err?.response?.data || {};
      setErrors(prev => ({ ...prev, ...srv }));
      toast.error(msg);
    } finally {
      if (mountedRef.current) { setLoading(false); setUploadProgress(null); }
    }
  };

  const combinedImagesForRender = () => {
    const existingPreviews = existingImages.map(buildImageUrl);
    const newPreviews = newFiles.map((f, i) => {
      const idx = existingImages.length + i;
      return imagePreviewsRef.current[idx] || (() => {
        const url = URL.createObjectURL(f);
        imagePreviewsRef.current[idx] = url;
        return url;
      })();
    });
    return [...existingPreviews, ...newPreviews];
  };

  useEffect(() => {
    if (existingImages.length && imagePreviewsRef.current.length === 0) {
      imagePreviewsRef.current = existingImages.map(buildImageUrl);
    }
  }, [existingImages]);

  const discount = calculateDiscount();

  return (
    <div className="p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900">Éditer le Deal</h1>
          <p className="text-gray-600 mt-1">Modifiez les informations</p>
        </div>

        <form onSubmit={handleSubmit} noValidate>
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Informations Principales</h2></div>
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Titre *</label>
                <input name="title" value={formData.title} onChange={handleChange} className={`w-full px-4 py-2 border rounded-lg ${errors.title ? 'border-red-300' : 'border-gray-300'}`} />
                {errors.title && <p className="mt-1 text-xs text-red-600">{errors.title}</p>}
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description *</label>
                <textarea name="description" value={formData.description} onChange={handleChange} rows="4" className={`w-full px-4 py-2 border rounded-lg ${errors.description ? 'border-red-300' : 'border-gray-300'}`} />
                {errors.description && <p className="mt-1 text-xs text-red-600">{errors.description}</p>}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Catégorie *</label>
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                    className={`w-full px-4 py-2 border rounded-lg ${errors.category ? 'border-red-300' : 'border-gray-300'}`}
                  >
                    <option value="">Sélectionner une catégorie</option>
                    {categories.map(cat => (
                      <option key={cat._id || cat.id} value={String(cat._id || cat.id)}>
                        {cat.name}
                      </option>
                    ))}
                  </select>
                  {errors.category && <p className="mt-1 text-xs text-red-600">{errors.category}</p>}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Localisation *</label>
                  <input name="location" value={formData.location} onChange={handleChange} className={`w-full px-4 py-2 border rounded-lg ${errors.location ? 'border-red-300' : 'border-gray-300'}`} />
                  {errors.location && <p className="mt-1 text-xs text-red-600">{errors.location}</p>}
                </div>
              </div>
            </div>
          </div>

          {/* pricing, dates, highlights, images, terms similar to CreateDeal but using combinedImagesForRender */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Prix et Remise</h2></div>
            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <input type="number" name="originalPrice" value={formData.originalPrice} onChange={handleChange} placeholder="Prix original" className={`px-4 py-2 border rounded-lg ${errors.originalPrice ? 'border-red-300' : 'border-gray-300'}`} />
                <input type="number" name="price" value={formData.price} onChange={handleChange} placeholder="Prix deal" className={`px-4 py-2 border rounded-lg ${errors.price ? 'border-red-300' : 'border-gray-300'}`} />
                <div className="flex items-center h-11 px-4 bg-green-50 border border-green-200 rounded-lg"><span className="text-lg font-bold text-green-600">-{discount}%</span></div>
              </div>

              {/* PRIX PARTENAIRE — voir CreateDeal.js pour le detail. Vide = commission. */}
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                <label className="block text-sm font-semibold text-amber-900 mb-1">Prix partenaire / prix d'achat (optionnel)</label>
                <div className="flex items-center gap-3">
                  <input type="number" min="0" step="0.01" name="partnerPrice" value={formData.partnerPrice} onChange={(e) => { setPartnerPriceTouched(true); handleChange(e); }} placeholder="Laisser vide = commission habituelle" className="flex-1 px-4 py-2 border border-amber-300 rounded-lg bg-white" />
                  <span className="text-sm font-semibold text-amber-900 shrink-0">MAD / unite</span>
                </div>
                {/* Avertissement UNIQUEMENT si une valeur EXISTE sans avoir pu
                    etre transmise (`partnerPriceHidden`, renvoye par le
                    serveur). Une annonce qui n'a jamais eu de prix partenaire
                    ne doit rien afficher : sinon l'alerte devient du bruit
                    qu'on n'ecoute plus le jour ou elle compte vraiment. */}
                {formData.partnerPriceHidden && !formData.partnerPriceLoaded && (
                  <p className="text-xs font-bold text-red-700 mt-2">
                    Prix partenaire non chargé — vous consultez cette annonce sans
                    les droits nécessaires (mode support, ou annonce d'un autre
                    marchand). Le champ reste vide à l'écran mais la valeur en base
                    ne sera PAS modifiée. Repassez en admin pour la voir et la
                    changer.
                  </p>
                )}
                <p className="text-xs text-amber-800 mt-2">Montant verse au partenaire pour chaque unite vendue, quel que soit le prix de vente. Vide = commission du pack.</p>
                {String(formData.partnerPrice).trim() !== '' && Number(formData.price) > 0 ? (
                  Number(formData.partnerPrice) > Number(formData.price) ? (
                    <p className="text-xs font-bold text-red-700 mt-2">Attention : le prix d'achat depasse le prix de vente. Perte de {(Number(formData.partnerPrice) - Number(formData.price)).toFixed(2)} MAD par unite.</p>
                  ) : (
                    <p className="text-xs font-bold text-green-700 mt-2">Votre marge : {(Number(formData.price) - Number(formData.partnerPrice)).toFixed(2)} MAD par unite.</p>
                  )
                ) : null}
                <p className="text-xs text-amber-700 mt-2">Ce montant n'apparait jamais sur le site public ni sur le coupon.</p>
              </div>
              <div>
                <div className="flex items-center gap-3">
                  <input type="number" name="quantity" value={formData.unlimited ? '' : formData.quantity} onChange={handleChange} disabled={formData.unlimited} placeholder={formData.unlimited ? 'Illimité' : 'Quantité'} className={`flex-1 px-4 py-2 border rounded-lg ${formData.unlimited ? 'bg-gray-50 text-gray-400' : ''} ${errors.quantity ? 'border-red-300' : 'border-gray-300'}`} />
                  <label className="flex items-center gap-2 shrink-0 cursor-pointer select-none text-sm font-medium text-gray-700">
                    <input type="checkbox" name="unlimited" checked={formData.unlimited} onChange={(e) => { const v = e.target.checked; setFormData((prev) => ({ ...prev, unlimited: v })); setErrors((prev) => ({ ...prev, quantity: undefined })); }} className="w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500" />
                    Illimité
                  </label>
                </div>
                {!formData.unlimited && errors.quantity && <p className="mt-1 text-xs text-red-600">{errors.quantity}</p>}
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Type d'annonce &amp; caractéristiques</h2></div>
            <div className="p-6 space-y-5">
              {/* Type d'annonce */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {[
                  { t: 'service', label: 'Service / Deal', Icon: FiTag, desc: 'Coupon, réservation, prestation' },
                  { t: 'physical', label: 'Produit physique', Icon: FiPackage, desc: 'Livraison, stock, variantes' },
                  { t: 'digital', label: 'Produit digital', Icon: FiDownloadCloud, desc: 'Accès remis en ligne' },
                ].map(({ t, label, Icon, desc }) => (
                  <button
                    key={t}
                    type="button"
                    onClick={() => setProductType(t)}
                    className={`text-left p-3 rounded-lg border transition ${formData.productType === t ? 'border-primary-600 bg-primary-50' : 'border-gray-300 hover:bg-gray-50'}`}
                  >
                    <div className="flex items-center gap-2 font-semibold text-gray-800"><Icon /> {label}</div>
                    <div className="text-xs text-gray-500 mt-1">{desc}</div>
                  </button>
                ))}
              </div>

              {/* ===== Fiche produit (non-services) ===== */}
              {formData.productType !== 'service' && (
                <div className="space-y-5 border-t pt-5">
                  <h3 className="font-semibold text-gray-900">Fiche produit</h3>

                  {formData.productType === 'physical' && (
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">État du produit</label>
                      <div className="flex flex-wrap gap-3">
                        {[{ key: 'new', label: 'Neuf' }, { key: 'used', label: 'Occasion' }].map((c) => (
                          <button
                            key={c.key}
                            type="button"
                            onClick={() => setFormData((prev) => ({ ...prev, condition: c.key }))}
                            className={`px-5 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                              formData.condition === c.key
                                ? 'border-primary-600 bg-primary-50 text-primary-700'
                                : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                            }`}
                          >
                            {c.label}
                          </button>
                        ))}
                      </div>
                      <p className="mt-2 text-xs text-gray-500">
                        Modifier l'état ou la marque renvoie l'annonce en validation : ce sont
                        des informations vues par l'acheteur.
                      </p>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Disponibilité</label>
                    <div className="flex flex-wrap gap-3">
                      {[
                        { key: 'in_stock', label: 'En stock' },
                        { key: 'out_of_stock', label: 'Rupture de stock' },
                        { key: 'incoming', label: 'En arrivage' }
                      ].map((a) => (
                        <button
                          key={a.key}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, availability: a.key }))}
                          className={`px-5 py-2 rounded-lg border text-sm font-semibold transition-colors ${
                            formData.availability === a.key
                              ? 'border-primary-600 bg-primary-50 text-primary-700'
                              : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                          }`}
                        >
                          {a.label}
                        </button>
                      ))}
                    </div>
                    {formData.availability !== 'in_stock' && (
                      <p className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                        L'annonce reste en ligne, mais les clients ne peuvent pas la commander.
                        Ce changement ne renvoie pas l'annonce en validation.
                      </p>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Marque</label>
                      <BrandPicker
                        value={{ brandId: formData.brandId, brand: formData.brand }}
                        onChange={(v) => setFormData((prev) => ({ ...prev, brandId: v.brandId, brand: v.brand }))}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Référence produit (SKU)</label>
                      <input type="text" name="sku" value={formData.sku} onChange={handleChange} maxLength={80} placeholder="Votre référence interne" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>

                  {formData.productType === 'physical' && (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Code-barres <span className="text-gray-400 font-normal">(facultatif)</span>
                        </label>
                        <input type="text" name="barcode" value={formData.barcode} onChange={handleChange} maxLength={40} placeholder="EAN / UPC" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Date de péremption</label>
                        <input type="date" name="expiryDate" value={formData.expiryDate} onChange={handleChange} className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                        <p className="mt-1 text-xs text-gray-500">Laissez vide si le produit ne périme pas.</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ===== Physique : livraison + caractéristiques ===== */}
              {formData.productType === 'physical' && (
                <div className="space-y-5 border-t pt-5">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Frais de livraison (MAD)</label>
                      <input type="number" name="shippingFee" value={formData.shippingFee} onChange={handleChange} placeholder="0" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Livraison gratuite dès (MAD)</label>
                      <input type="number" name="freeShippingThreshold" value={formData.freeShippingThreshold} onChange={handleChange} placeholder="Optionnel" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Poids (grammes)</label>
                      <input type="number" name="weightGrams" value={formData.weightGrams} onChange={handleChange} placeholder="Optionnel" className="w-full px-4 py-2 border border-gray-300 rounded-lg" />
                    </div>
                  </div>

                  {/* Lot D — Mode de tarif de livraison */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mode de tarif de livraison</label>
                    <div className="flex gap-2">
                      {[{ k: 'flat', label: 'Tarif fixe' }, { k: 'weight', label: 'Au poids' }].map((m) => (
                        <button
                          key={m.k}
                          type="button"
                          onClick={() => setFormData((prev) => ({ ...prev, shippingMode: m.k }))}
                          className={`px-4 py-2 rounded-lg text-sm font-medium border transition ${formData.shippingMode === m.k ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    {formData.shippingMode === 'weight' && (
                      <div className="mt-3 border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium text-gray-700">Paliers de poids</span>
                          <button type="button" onClick={addTier} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"><FiPlus /> Ajouter un palier</button>
                        </div>
                        <p className="text-xs text-gray-400 mb-2">Le tarif appliqué est celui du premier palier dont le poids « jusqu'à » couvre le poids total de la commande (poids unitaire × quantité). Au-delà du dernier palier, c'est le palier le plus lourd qui s'applique. Le poids unitaire est le champ « Poids (grammes) » ci-dessus.</p>
                        <div className="space-y-2">
                          {(formData.shippingWeightTiers || []).map((t, i) => (
                            <div key={i} className="flex items-center gap-2">
                              <div className="flex-1 flex items-center gap-1">
                                <span className="text-xs text-gray-500 whitespace-nowrap">jusqu'à</span>
                                <input type="number" value={t.uptoGrams} onChange={(e) => updateTier(i, 'uptoGrams', e.target.value)} placeholder="g" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                <span className="text-xs text-gray-500">g</span>
                              </div>
                              <div className="flex-1 flex items-center gap-1">
                                <input type="number" value={t.fee} onChange={(e) => updateTier(i, 'fee', e.target.value)} placeholder="Frais" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                                <span className="text-xs text-gray-500">MAD</span>
                              </div>
                              <button type="button" onClick={() => removeTier(i)} className="text-gray-400 hover:text-red-600"><FiTrash2 /></button>
                            </div>
                          ))}
                          {(formData.shippingWeightTiers || []).length === 0 && (
                            <p className="text-xs text-gray-400">Aucun palier — le tarif fixe ci-dessus sera utilisé en repli.</p>
                          )}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Zones de livraison */}
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <label className="block text-sm font-medium text-gray-700">Zones de livraison (optionnel)</label>
                      <button type="button" onClick={addZone} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"><FiPlus /> Ajouter une zone</button>
                    </div>
                    <p className="text-xs text-gray-400 mb-2">Frais spécifiques par ville. Les villes non listées utilisent le tarif par défaut ci-dessus.</p>
                    <div className="space-y-3">
                      {(formData.shippingZones || []).map((z, i) => (
                        <div key={i} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60 space-y-2">
                          <div className="flex items-center gap-2">
                            <input value={z.name} onChange={(e) => updateZone(i, 'name', e.target.value)} placeholder="Nom de la zone (ex. Grand Casablanca)" className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                            <button type="button" onClick={() => removeZone(i)} className="text-gray-400 hover:text-red-600"><FiTrash2 /></button>
                          </div>
                          <input value={(z.cities || []).join(', ')} onChange={(e) => updateZoneCities(i, e.target.value)} placeholder="Villes séparées par des virgules — ex. Casablanca, Mohammedia" className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          <div className="grid grid-cols-2 gap-2">
                            <input type="number" value={z.fee} onChange={(e) => updateZone(i, 'fee', e.target.value)} placeholder="Frais (MAD)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                            <input type="number" value={z.freeThreshold} onChange={(e) => updateZone(i, 'freeThreshold', e.target.value)} placeholder="Gratuit dès (MAD)" className="px-3 py-2 border border-gray-300 rounded-lg text-sm" />
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Caractéristiques intelligentes */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Caractéristiques du produit</label>
                    <p className="text-xs text-gray-400 mb-3">Activez ce qui correspond à votre produit, puis générez les variantes automatiquement.</p>
                    <div className="flex flex-wrap gap-2 mb-4">
                      {ATTR_ORDER.map((type) => {
                        const active = !!getAttr(type);
                        return (
                          <button key={type} type="button" onClick={() => toggleAttribute(type)}
                            className={`px-3 py-1.5 rounded-full text-sm font-medium border transition ${active ? 'border-primary-600 bg-primary-50 text-primary-700' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                            {active ? '✓ ' : '+ '}{ATTR_DEFS[type].label}
                          </button>
                        );
                      })}
                    </div>
                    <div className="space-y-3">
                      {(formData.attributes || []).map((a) => {
                        const def = ATTR_DEFS[a.type];
                        if (!def) return null;
                        return (
                          <div key={a.type} className="border border-gray-200 rounded-lg p-3 bg-gray-50/60">
                            <div className="flex items-center justify-between mb-2">
                              <div className="flex items-center gap-3">
                                <span className="text-sm font-semibold text-gray-800">{def.label}</span>
                                {def.units && (
                                  <select value={a.unit || def.units[0]} onChange={(e) => setAttrUnit(a.type, e.target.value)} className="text-xs border border-gray-300 rounded px-2 py-1 bg-white">
                                    {def.units.map((u) => <option key={u} value={u}>{u}</option>)}
                                  </select>
                                )}
                              </div>
                              <button type="button" onClick={() => toggleAttribute(a.type)} className="text-gray-400 hover:text-red-600" title="Retirer"><FiX /></button>
                            </div>
                            {def.presets && def.presets.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mb-2">
                                {def.presets.map((p) => {
                                  const already = a.values.includes(p);
                                  return (
                                    <button key={p} type="button" onClick={() => already ? removeAttrValue(a.type, p) : addAttrValue(a.type, p)}
                                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-xs border transition ${already ? 'border-primary-500 bg-primary-100 text-primary-700' : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-100'}`}>
                                      {def.kind === 'color' && <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: COLOR_HEX[p] || '#ddd' }} />}
                                      {def.kind === 'measure' ? `${p}${a.unit || ''}` : p}
                                    </button>
                                  );
                                })}
                              </div>
                            )}
                            <div className="flex gap-2 mb-2">
                              <input value={attrInputs[a.type] || ''} onChange={(e) => setAttrInputs((s) => ({ ...s, [a.type]: e.target.value }))}
                                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addAttrValue(a.type, attrInputs[a.type]); setAttrInputs((s) => ({ ...s, [a.type]: '' })); } }}
                                placeholder={def.ph || 'Ajouter une valeur'} className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg text-sm bg-white" />
                              <button type="button" onClick={() => { addAttrValue(a.type, attrInputs[a.type]); setAttrInputs((s) => ({ ...s, [a.type]: '' })); }}
                                className="px-3 py-1.5 rounded-lg bg-gray-900 text-white text-sm font-medium hover:opacity-90">Ajouter</button>
                            </div>
                            {a.values.length > 0 && (
                              <div className="flex flex-wrap gap-1.5">
                                {a.values.map((v) => (
                                  <span key={v} className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-300 text-xs text-gray-700">
                                    {def.kind === 'color' && <span className="w-3 h-3 rounded-full border border-gray-300" style={{ backgroundColor: COLOR_HEX[v] || '#ddd' }} />}
                                    {def.kind === 'measure' ? `${v}${a.unit || ''}` : v}
                                    <button type="button" onClick={() => removeAttrValue(a.type, v)} className="text-gray-400 hover:text-red-600"><FiX size={12} /></button>
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    {(formData.attributes || []).some(a => a.values && a.values.length) && (
                      <button type="button" onClick={generateVariants} className="mt-3 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 text-white text-sm font-semibold hover:bg-primary-700">
                        <FiPackage /> Générer les variantes
                      </button>
                    )}
                  </div>

                  {/* Variantes : prix / stock */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="block text-sm font-medium text-gray-700">Variantes &amp; prix / stock</label>
                      <button type="button" onClick={addVariant} className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"><FiPlus /> Ajouter une variante</button>
                    </div>
                    {(formData.variants || []).length === 0 && (
                      <p className="text-xs text-gray-400">Générez les variantes ci-dessus, ou ajoutez-les manuellement.</p>
                    )}
                    <div className="space-y-2">
                      {(formData.variants || []).map((v, i) => (
                        <div key={i} className="grid grid-cols-12 gap-2 items-start">
                          <input value={v.label} onChange={(e) => updateVariant(i, 'label', e.target.value)} placeholder="Libellé" className="col-span-6 px-3 py-2 border border-gray-300 rounded-lg" />
                          <input type="number" value={v.priceDelta} onChange={(e) => updateVariant(i, 'priceDelta', e.target.value)} placeholder="+/- prix" className="col-span-3 px-3 py-2 border border-gray-300 rounded-lg" />
                          <input type="number" value={v.stock} onChange={(e) => updateVariant(i, 'stock', e.target.value)} placeholder="Stock" className="col-span-2 px-3 py-2 border border-gray-300 rounded-lg" />
                          <button type="button" onClick={() => removeVariant(i)} className="col-span-1 h-10 flex items-center justify-center text-gray-400 hover:text-red-600"><FiTrash2 /></button>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Période de Validité</h2></div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de début</label>
                  <input type="date" name="validFrom" value={formData.validFrom} onChange={handleChange} className="w-full px-4 py-2 border rounded-lg border-gray-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Date de fin</label>
                  <input
                    type="date"
                    name="validUntil"
                    value={formData.validUntil}
                    onChange={handleChange}
                    disabled={formData.openEnded}
                    className={`w-full px-4 py-2 border rounded-lg border-gray-300 ${formData.openEnded ? 'bg-gray-100 text-gray-400 cursor-not-allowed' : ''}`}
                  />
                </div>
              </div>
              {/* ✅ POINT 5 : validité ouverte */}
              <label className="mt-4 flex items-center gap-2 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={formData.openEnded}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setFormData(prev => ({ ...prev, openEnded: checked, validUntil: checked ? '' : prev.validUntil }));
                    setErrors(prev => ({ ...prev, validUntil: undefined }));
                  }}
                  className="h-4 w-4"
                />
                <span className="text-sm text-gray-700">Validité ouverte (sans date de fin)</span>
              </label>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b flex justify-between">
              <h2 className="text-xl font-semibold">Points Forts</h2>
              <button type="button" onClick={addHighlight} className="px-3 py-1 bg-primary-600 text-white rounded">Ajouter</button>
            </div>
            <div className="p-6 space-y-3">
              {formData.highlights.map((h, i) => (
                <div key={i} className="flex items-center space-x-2">
                  <input value={h} onChange={(e) => handleHighlightChange(i, e.target.value)} className="flex-1 px-4 py-2 border rounded-lg" />
                  {formData.highlights.length > 1 && <button type="button" onClick={() => removeHighlight(i)} className="p-2 text-red-600"><FiX /></button>}
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Images</h2></div>
            <div className="p-6">
              <div>
                <p className="text-xs text-gray-500 mb-2">{'Recommandé : 880 × 340 px, < 300 Ko'}</p>
                <input id="image-upload" type="file" multiple accept="image/*" onChange={handleImageUpload} className="hidden" />
                <label htmlFor="image-upload" className="block border-2 border-dashed border-indigo-400 bg-indigo-50 rounded-xl p-8 text-center cursor-pointer hover:bg-indigo-100 hover:border-indigo-500 transition">
                  <FiUploadCloud className="mx-auto text-indigo-500 mb-2" size={38} />
                  <p className="text-sm font-medium text-indigo-700">Glissez vos images ou cliquez (max {MAX_IMAGES})</p>
                  <span className="mt-3 inline-block px-5 py-2 bg-indigo-600 text-white text-sm font-semibold rounded-lg pointer-events-none">Choisir des images</span>
                </label>
                {errors.images && <p className="mt-2 text-xs text-red-600">{errors.images}</p>}

                {imageOrder.length > 0 && (
                  <p className="mt-4 text-xs text-gray-500">
                    Glissez pour réordonner (ou utilisez les flèches au survol).
                    La 1&#7523;&#7497; image est l'image principale du produit.
                  </p>
                )}
                <div className="mt-2 grid grid-cols-3 gap-3">
                  {imageOrder.map((token, idx) => {
                    const url = previewFor(token);
                    return (
                      <div
                        key={token}
                        draggable
                        onDragStart={() => setDragIdx(idx)}
                        onDragOver={(e) => e.preventDefault()}
                        onDrop={(e) => { e.preventDefault(); reorderImages(dragIdx, idx); setDragIdx(null); }}
                        onDragEnd={() => setDragIdx(null)}
                        className={`relative border rounded overflow-hidden group bg-white cursor-grab active:cursor-grabbing transition ${dragIdx === idx ? 'opacity-40 ring-2 ring-primary-400' : 'hover:ring-1 hover:ring-gray-300'}`}
                      >
                        {url
                          ? <img src={url} alt={`photo-${idx}`} draggable={false} className="w-full h-32 object-cover select-none" />
                          : <div className="w-full h-32 bg-gray-100 flex items-center justify-center">No image</div>}
                        <span className={`absolute top-1 left-1 inline-flex items-center gap-1 text-[10px] font-bold px-1.5 py-0.5 rounded ${idx === 0 ? 'bg-primary-600 text-white' : 'bg-black/55 text-white'}`}>
                          {idx === 0 ? 'Principale' : idx + 1}
                        </span>
                        <button type="button" onClick={() => removeByToken(token)} title="Supprimer"
                          className="absolute top-1 right-1 bg-white rounded-full p-1 text-red-600 shadow-sm hover:bg-red-50">
                          <FiX />
                        </button>
                        <div className="absolute bottom-1 inset-x-1 flex justify-between opacity-0 group-hover:opacity-100 focus-within:opacity-100 transition">
                          <button type="button" disabled={idx === 0} onClick={() => reorderImages(idx, idx - 1)}
                            title="Déplacer avant" aria-label="Déplacer avant"
                            className="bg-white/95 rounded-full px-2 py-1 text-xs font-bold text-gray-700 shadow-sm disabled:opacity-30 hover:bg-white">&#8592;</button>
                          <button type="button" disabled={idx === imageOrder.length - 1} onClick={() => reorderImages(idx, idx + 1)}
                            title="Déplacer après" aria-label="Déplacer après"
                            className="bg-white/95 rounded-full px-2 py-1 text-xs font-bold text-gray-700 shadow-sm disabled:opacity-30 hover:bg-white">&#8594;</button>
                        </div>
                      </div>
                    );
                  })}
                </div>

                {(() => {
                  const colorAttr = (formData.attributes || []).find((a) => a.type === 'color' && Array.isArray(a.values) && a.values.length);
                  const previews = combinedImagesForRender();
                  if (!colorAttr || previews.length === 0) return null;
                  return (
                    <div className="mt-6 border-t pt-4">
                      <div className="flex items-center justify-between gap-3 mb-1">
                        <label className="block text-sm font-medium text-gray-700">Image par couleur (optionnel)</label>
                        <button type="button" onClick={autoDetectColors} disabled={detecting}
                          className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1.5 rounded-md bg-primary-50 text-primary-700 hover:bg-primary-100 disabled:opacity-60 whitespace-nowrap">
                          {detecting ? <FiLoader className="animate-spin" size={13} /> : <FiZap size={13} />}
                          {detecting ? 'Analyse…' : 'Détecter automatiquement'}
                        </button>
                      </div>
                      <p className="text-xs text-gray-400 mb-3">Associez une photo à chaque couleur : sur la fiche produit, choisir la couleur affichera automatiquement cette image. « Détecter automatiquement » analyse la couleur dominante de chaque image et propose l'association.</p>
                      <div className="space-y-3">
                        {colorAttr.values.map((c, ci) => (
                          <div key={ci} className="flex items-start gap-3 flex-wrap">
                            <span className="inline-flex items-center gap-1.5 text-sm font-medium text-gray-700 w-32 pt-1">
                              <span className="w-4 h-4 rounded-full border border-gray-300 shrink-0" style={{ backgroundColor: COLOR_HEX[c] || '#ddd' }} />
                              {c}
                            </span>
                            <div className="flex flex-wrap gap-2 flex-1">
                              {previews.map((url, idx) => {
                                const sel = Number(formData.colorImageMap?.[c]) === idx;
                                return (
                                  <button key={idx} type="button" onClick={() => setColorImage(c, idx)} title={`Image ${idx + 1}`}
                                    className={`w-12 h-12 rounded-md overflow-hidden border-2 transition ${sel ? 'border-primary-600 ring-2 ring-primary-200' : 'border-gray-200 hover:border-gray-300'}`}>
                                    {url ? <img src={url} alt={`img-${idx}`} className="w-full h-full object-cover" /> : <span className="text-[10px] flex items-center justify-center w-full h-full">{idx + 1}</span>}
                                  </button>
                                );
                              })}
                              {formData.colorImageMap?.[c] != null && (
                                <button type="button" onClick={() => setColorImage(c, null)} className="text-xs text-gray-400 hover:text-red-600 self-center">retirer</button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            </div>
          </div>

          {formData.status === 'rejected' && formData.rejectionReason && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-red-900">
                <strong>Annonce refusée — motif :</strong> {formData.rejectionReason}
              </p>
              <p className="text-xs text-red-700 mt-1">
                Corrigez les points signalés puis enregistrez : l'annonce repartira
                automatiquement en validation.
              </p>
            </div>
          )}

          {formData.status === 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-900">
                <strong>En attente de validation :</strong> cette annonce n'est pas encore
                visible du public. Un administrateur doit la valider avant sa publication.
              </p>
            </div>
          )}

          {/* Validation admin obligatoire : prévenir avant que le marchand ne modifie */}
          {formData.status !== 'pending' && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
              <p className="text-sm text-yellow-900">
                <strong>Modération :</strong> modifier le titre, la description, les photos,
                le prix ou la catégorie renvoie cette annonce en attente de validation par un
                administrateur. Elle sera retirée du site le temps du contrôle. Le stock et les
                dates de validité peuvent être ajustés sans repasser en validation.
              </p>
            </div>
          )}

          {/* ===== Lot P1 — Infos de prestation (communiquées sur le coupon) ===== */}
          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b">
              <h2 className="text-xl font-semibold">Infos de prestation</h2>
              <p className="text-sm text-gray-500 mt-1">
                L'adresse exacte et les consignes ne sont <strong>jamais affichées publiquement</strong> :
                elles sont communiquées au client sur son coupon, <strong>après paiement</strong>.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <ServiceLocationPicker
                value={formData.serviceLocationId}
                onApply={applyServiceLocation}
              />

              {/* MULTI-VILLES : lieux ou cette annonce est disponible.
                  Le lieu principal ci-dessus alimente l'adresse et les horaires
                  de la fiche ; ceux coches ici s'y ajoutent, et le client
                  choisira le sien au moment de l'achat. */}
              <DealCitiesPicker
                value={formData.serviceLocationIds || []}
                onChange={(ids) => setFormData((p) => ({ ...p, serviceLocationIds: ids }))}
                disabled={locked}
              />
              <div>
                <label className="block text-sm font-medium mb-1">
                  Adresse exacte {formData.productType === 'service' && <span className="text-red-500">*</span>}
                </label>
                <input type="text" name="addressFull" value={formData.addressFull} onChange={handleChange}
                  readOnly={locked}
                  placeholder="Ex : 12 Rue Beethoven, Résidence Oumnia, Bureau 83, Tanger"
                  className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`} />
                {errors.addressFull && <p className="text-red-500 text-sm mt-1">{errors.addressFull}</p>}
                <p className="text-xs text-gray-500 mt-1">Adresse où le client se présente. Communiquée après l'achat.</p>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Horaires d'ouverture</label>
                  <input type="text" name="openingHours" value={formData.openingHours} onChange={handleChange}
                    readOnly={locked} placeholder="Ex : Lun–Sam 10h–19h"
                    className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`} />
                  <p className="text-xs text-gray-500 mt-1">Affiché publiquement sur la fiche.</p>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Durée de la prestation</label>
                  <div className="relative">
                    <input type="number" min="1" name="durationMinutes" value={formData.durationMinutes}
                      onChange={handleChange} placeholder="60" className="w-full px-4 py-2 border rounded-lg pr-14" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm">min</span>
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Latitude (facultatif)</label>
                  <input type="text" name="addressLat" value={formData.addressLat} onChange={handleChange}
                    readOnly={locked} placeholder="35.7595"
                    className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`} />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">Longitude (facultatif)</label>
                  <input type="text" name="addressLng" value={formData.addressLng} onChange={handleChange}
                    readOnly={locked} placeholder="-5.8340"
                    className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`} />
                </div>
              </div>
              <p className="text-xs text-gray-500 -mt-2">
                Coordonnées GPS : permettent au client d'ouvrir l'itinéraire depuis son coupon.
              </p>
              <div>
                <label className="block text-sm font-medium mb-1">Consignes de présentation</label>
                <textarea name="instructions" value={formData.instructions} onChange={handleChange} rows="3"
                  readOnly={locked}
                  placeholder="Ex : Se présenter 10 minutes avant, demander l'accueil au 1er étage."
                  className={`w-full px-4 py-2 border rounded-lg ${locked ? 'bg-gray-100 text-gray-600' : ''}`} />
                <p className="text-xs text-gray-500 mt-1">Communiquées au client sur son coupon, après paiement.</p>
              </div>

              {/* Saisie unique : mémoriser ce lieu pour les prochaines annonces */}
              {!formData.serviceLocationId && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" name="saveAsServiceLocation" className="mt-1"
                      checked={!!formData.saveAsServiceLocation}
                      onChange={(e) => setFormData((f) => ({ ...f, saveAsServiceLocation: e.target.checked }))} />
                    <span className="text-sm text-gray-800">
                      Enregistrer ce lieu dans mon profil pour le réutiliser
                      <span className="block text-xs text-gray-500 mt-0.5">
                        Vos prochaines annonces reprendront automatiquement cette adresse, ces horaires
                        et ces consignes. Vous n'aurez plus à les ressaisir.
                      </span>
                    </span>
                  </label>
                  {formData.saveAsServiceLocation && (
                    <input type="text" name="serviceLocationLabel" value={formData.serviceLocationLabel}
                      onChange={handleChange} placeholder="Nom du lieu (ex : Salon Tanger Centre)"
                      className="mt-3 w-full px-4 py-2 border rounded-lg bg-white" />
                  )}
                </div>
              )}
            </div>
          </div>

          <div className="bg-white rounded-lg shadow mb-6">
            <div className="p-6 border-b"><h2 className="text-xl font-semibold">Conditions</h2></div>
            <div className="p-6">
              <textarea name="terms" value={formData.terms} onChange={handleChange} rows="4" className="w-full px-4 py-2 border rounded-lg" />
            </div>
          </div>

          <QuantityTiersEditor
            value={formData.quantityTiers}
            onChange={(v) => setFormData((f) => ({ ...f, quantityTiers: v }))}
            unitPrice={Number(formData.price) || 0}
          />

          <InfoSectionsEditor
            value={formData.infoSections}
            onChange={(v) => setFormData((f) => ({ ...f, infoSections: v }))}
          />

          <div className="flex justify-end space-x-4">
            <button type="button" onClick={() => navigate((user && (typeof user.role === 'string' ? user.role.toLowerCase().includes('admin') : false)) ? '/admin/deals' : '/merchant/deals')} className="px-6 py-3 border rounded">Annuler</button>
            <button type="submit" disabled={loading} className="flex items-center px-6 py-3 bg-primary-600 text-white rounded">
              {loading ? (uploadProgress !== null ? `Envoi ${uploadProgress}%` : 'Envoi...') : (<><FiSave className="mr-2" />Enregistrer</>)}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditDeal;
